import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Clock, CheckCircle, XCircle, Calendar, User, CreditCard } from 'lucide-react';
import { axiosInstance } from '../../lib/api-client';
import { toast } from 'sonner';

interface Commission {
  _id: string;
  userId: {
    firstName: string;
    lastName: string;
    email: string;
  };
  planId: {
    name: string;
    duration: number;
  };
  orderId?: {
    razorpayOrderId?: string;
    razorpayPaymentId?: string;
  };
  discountedPrice: number;
  commissionEarned: number;
  commissionPaid: boolean;
  commissionRejected?: boolean;
  commissionPaidDate?: string;
  commissionRejectionReason?: string;
  paymentDate?: string;
  createdAt: string;
}

interface CommissionSummary {
  totalEarned: number;
  totalPaid: number;
  totalPending: number;
  completedCount: number;
  paidCount: number;
}

export default function CommissionsListView() {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [summary, setSummary] = useState<CommissionSummary>({
    totalEarned: 0,
    totalPaid: 0,
    totalPending: 0,
    completedCount: 0,
    paidCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'paid' | 'pending'>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCommissions();
  }, [filter, page]);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (filter !== 'all') {
        params.status = filter;
      }

      const response = await axiosInstance.get('/promotional-orders/my-commissions', { params });

      if (response.data.success) {
        setCommissions(response.data.data.commissions);
        setSummary(response.data.data.summary);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch commissions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (commission: Commission) => {
    if (commission.commissionRejected) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Rejected
        </span>
      );
    }
    if (commission.commissionPaid) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Paid
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Pending
      </span>
    );
  };

  if (loading && commissions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-rose-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-600 text-sm font-medium">Total Earned</span>
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-900">₹{summary.totalEarned.toLocaleString()}</p>
          <p className="text-xs text-blue-600 mt-1">{summary.completedCount} completed orders</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-green-600 text-sm font-medium">Total Paid</span>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">₹{summary.totalPaid.toLocaleString()}</p>
          <p className="text-xs text-green-600 mt-1">{summary.paidCount} payments received</p>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-yellow-600 text-sm font-medium">Pending</span>
            <Clock className="w-5 h-5 text-yellow-600" />
          </div>
          <p className="text-2xl font-bold text-yellow-900">₹{summary.totalPending.toLocaleString()}</p>
          <p className="text-xs text-yellow-600 mt-1">Awaiting approval</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-purple-600 text-sm font-medium">Avg Commission</span>
            <TrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-purple-900">
            ₹{summary.completedCount > 0 ? Math.round(summary.totalEarned / summary.completedCount).toLocaleString() : 0}
          </p>
          <p className="text-xs text-purple-600 mt-1">Per order</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'paid', 'pending'].map((status) => (
          <button
            key={status}
            onClick={() => {
              setFilter(status as typeof filter);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Commissions List */}
      {commissions.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No commissions found</p>
          <p className="text-sm text-gray-500 mt-1">Commissions will appear here once users complete promotional orders</p>
        </div>
      ) : (
        <div className="space-y-4">
          {commissions.map((commission) => (
            <div
              key={commission._id}
              className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1 space-y-3">
                  {/* Customer Info */}
                  <div className="flex items-start gap-3">
                    <div className="bg-rose-100 rounded-full p-2">
                      <User className="w-5 h-5 text-rose-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {commission.userId.firstName} {commission.userId.lastName}
                      </h4>
                      <p className="text-sm text-gray-600">{commission.userId.email}</p>
                    </div>
                  </div>

                  {/* Plan Info */}
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <CreditCard className="w-4 h-4" />
                      <span>{commission.planId.name}</span>
                    </div>
                    <span className="text-gray-400">•</span>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{commission.planId.duration} months</span>
                    </div>
                    <span className="text-gray-400">•</span>
                    <span>Order: ₹{commission.discountedPrice.toLocaleString()}</span>
                  </div>

                  {/* Dates */}
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                    <span>Created: {formatDate(commission.createdAt)}</span>
                    {commission.commissionPaidDate && (
                      <span>Paid: {formatDate(commission.commissionPaidDate)}</span>
                    )}
                  </div>

                  {/* Rejection Reason */}
                  {commission.commissionRejected && commission.commissionRejectionReason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-800">
                        <strong>Rejection Reason:</strong> {commission.commissionRejectionReason}
                      </p>
                    </div>
                  )}
                </div>

                {/* Commission Amount & Status */}
                <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2">
                  <div className="text-right">
                    <p className="text-xs text-gray-500 mb-1">Commission</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ₹{commission.commissionEarned.toLocaleString()}
                    </p>
                  </div>
                  {getStatusBadge(commission)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}